import React from "react";
import "./numberStepper.css";

/**
 * Custom number stepper — read-only value with up/down arrow buttons.
 * Dynamically sizes to fit its container height.
 *
 * @param {string|number} value    - Current value
 * @param {function} onChange      - Callback with new numeric value
 * @param {string} [label]        - Optional label text above the input
 * @param {number} [min=1]        - Minimum allowed value
 * @param {number} [max=99]       - Maximum allowed value
 * @param {number} [step=1]       - Increment/decrement step
 * @param {object} [style]        - Optional style overrides on the wrapper
 * @param {boolean} [disabled]    - Lock both arrows (e.g. while submitting)
 * @param {string} [ariaLabel]    - Accessible name when there is no `label`
 */
export default function NumberStepper({
  value,
  onChange,
  label,
  min = 1,
  max = 99,
  step = 1,
  style,
  disabled = false,
  ariaLabel,
}) {
  const numVal = parseInt(value) || 0;

  const increment = () => {
    const next = numVal + step;
    if (next <= max) onChange(next);
  };

  const decrement = () => {
    const next = numVal - step;
    if (next >= min) onChange(next);
  };

  return (
    <div
      className={`num-stepper${disabled ? " num-stepper--disabled" : ""}`}
      style={style}
    >
      {label && <span className="num-stepper-label">{label}</span>}
      <div className="num-stepper-control">
        {/* The value is read-only by design — the arrows are the only way in,
            which is why it carries the accessible name and a live region. */}
        <div
          className="num-stepper-display"
          role="status"
          aria-live="polite"
          aria-label={ariaLabel || label}
        >
          {numVal || ""}
        </div>
        <div className="num-stepper-arrows">
          <button
            type="button"
            className="num-stepper-btn up"
            onClick={increment}
            disabled={disabled || numVal >= max}
            aria-label={`Increase${ariaLabel || label ? ` ${ariaLabel || label}` : ""}`}
          >
            <svg viewBox="0 0 10 6" className="num-stepper-icon">
              <path d="M1 5L5 1L9 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </button>
          <button
            type="button"
            className="num-stepper-btn down"
            onClick={decrement}
            disabled={disabled || numVal <= min}
            aria-label={`Decrease${ariaLabel || label ? ` ${ariaLabel || label}` : ""}`}
          >
            <svg viewBox="0 0 10 6" className="num-stepper-icon">
              <path d="M1 1L5 5L9 1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
